import { Component, OnInit, OnDestroy, HostListener, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { AppLayout, MenuItem, NavOptions } from '@powerstandards/powerside-ui-lib';
import { MenuModule } from 'primeng/menu';
import moment from 'moment';
import { Dialog } from 'primeng/dialog';
import { filter, forkJoin, map, Observable, of, shareReplay, take } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { cloneDeep } from 'lodash';

import packageJson from '../../../package.json';
import { environment } from 'src/environments/environment';
import { OnDestroyMixin, untilComponentDestroyed } from 'src/app/_shared/classes/component-destroy.class';
import * as fromStore from 'src/app/_store/_reducers';
import { NewsActions } from 'src/app/_store/_news/actions';
import {
  ChangeAccountSelectionFeature,
  FirmwareUpdateFeature,
  SarfiAnalysisFeature,
} from 'src/app/_shared/classes/features';
import { ChangeSelectionComponent } from 'src/app/app-shell/change-selection/change-selection.component';
import { HeaderNamePipe } from 'src/app/_shared/pipes/header-name.pipe';
import { MpOptions } from 'src/app/_shared/interface/mp-options.interface';
import { canSelectFromPartners } from 'src/app/_shared/interface/partner.interfacre';
import { UserActions } from 'src/app/_store/_user/actions';
import { NotificationsService } from 'src/app/_shared/modules/notifications/shared/notifications.service';
import { AuthService } from 'src/app/_shared/services';
import { IUser } from 'src/app/_shared/classes/user';
import { IMeasurementPoint } from 'src/app/_shared/classes/measurementpoint.interface';
import { UserPreferences } from 'src/app/_shared/interface/user-preferences.interface';
import { NotificationGroupFeature, RmsAnalysisFeature } from 'src/app/_shared/classes/features';
import { ReleaseNewsComponent } from 'src/app/app-shell/release-news/release-news.component';
import { WelcomeDialogComponent } from 'src/app/app-shell/welcome-dialog/welcome-dialog.component';
import { EventTrackingService } from 'src/app/_shared/tracking/event-tracking.service';
import { HintBoxComponent } from 'src/app/_shared/components/hint-box/hint-box.component';
import { InitActions } from 'src/app/_store/_init/actions';
import { LoaderComponent } from 'src/app/_shared/components/loader/loader.component';
import { InactivityService } from 'src/app/_shared/services/inactivity.service';
import { HeadBannerComponent } from 'src/app/app-shell/head-banner/head-banner.component';
import { MpState, MpType } from 'src/app/_shared/enum/measurementPoint.enum';
import { FleetDisturbanceAnalysisType } from 'src/app/_shared/classes/fleetDisturbanceAnalysis';

@Component({
  selector: 'app-app-shell',
  imports: [
    LoaderComponent,
    AppLayout,
    HintBoxComponent,
    TranslateModule,
    MatIconModule,
    AsyncPipe,
    ButtonModule,
    TooltipModule,
    RouterLink,
    CdkOverlayOrigin,
    ChangeSelectionComponent,
    MenuModule,
    HeaderNamePipe,
    TooltipModule,
    SelectModule,
    FormsModule,
    NgClass,
    Dialog,
    WelcomeDialogComponent,
    ReleaseNewsComponent,
    CommonModule,
    HeadBannerComponent,
  ],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent extends OnDestroyMixin implements OnDestroy, OnInit {
  protected readonly FleetDisturbanceAnalysisType = FleetDisturbanceAnalysisType;
  navOptions: Array<NavOptions & { featureKey?: string }> = [];

  menuItems: Array<MenuItem> = [];

  @Output() actionTrigger = new EventEmitter();
  queryParams: Params;
  trackingEnabled: boolean = false;
  current;
  firstName;
  version = packageJson.version;
  isSysAdmin: boolean;
  isSalesOps: boolean;
  isEngineer: boolean;
  firmwareUpdateFeature$: Observable<FirmwareUpdateFeature>;
  notificationCenterFeature$: Observable<NotificationGroupFeature>;
  rmsVariationAnalysisFeature$: Observable<RmsAnalysisFeature>;
  sarfiAnalysisFeature$: Observable<SarfiAnalysisFeature>;
  changeAccountSelectionFeature$: Observable<ChangeAccountSelectionFeature>;
  hintFirmwareIsOpened: boolean;
  hintNotificationIsOpened: boolean;
  hintFirmwareAndNotificationIsOpened: boolean;
  hintRmsIsOpened: boolean;
  isSysAdmin$: Observable<boolean>;
  viewerMode$: Observable<boolean>;
  smallScreen = window.innerWidth < 992;
  dynamicMargin = { sectionTop: this.smallScreen ? '89px' : '67px', navigationTop: this.smallScreen ? '89px' : '67px' };
  premiumFeatureItemSmall = [];
  showMp: boolean = true;
  lastMpOpen = false;
  mp: IMeasurementPoint;
  mpId: number;
  user: IUser;
  headerName$: Observable<string>;
  mpOptions: Array<MpOptions> = [];
  canSelectMp: Observable<boolean>;
  showChangeSelectionDialog = false;
  showWelcomeDialog = false;
  showReleaseDialog = false;
  hintIsOpened = true;
  hintReportIsOpened = true;
  mobileMenuOpen = false;
  possibleHint = false;
  loadingMp;

  private accountId: number;
  private userPrefs: UserPreferences;
  private currentHeadBannerHeight: number = 0;
  private readonly DYNAMIC_MARGIN_SMALL = 75;
  private readonly DYNAMIC_MARGIN_LARGE = 67;

  @HostListener('window:resize')
  onResize(): void {
    this.smallScreen = window.innerWidth < 992;
    this.setDynamicMargin(
      this.currentHeadBannerHeight + (this.smallScreen ? this.DYNAMIC_MARGIN_SMALL : this.DYNAMIC_MARGIN_LARGE)
    );
  }

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private eventTrackingService: EventTrackingService,
    protected store: Store<fromStore.State>,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private inactivityService: InactivityService
  ) {
    super();
    this.router.events
      .pipe(
        untilComponentDestroyed(this),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((u: NavigationEnd) => {
        if (u.url) {
          this.current = u.url.split('?').shift();
          this.showMp =
            (!window.location.href.includes('/notification') &&
              !window.location.href.includes('/admin') &&
              !window.location.href.includes('/manage-member') &&
              !window.location.href.includes('/sales-ops') &&
              !window.location.href.includes('/system-admin') &&
              !window.location.href.includes('/rms-variation-analysis')) ||
            window.location.href.includes('/customer');
        }
      });
    this.trackingEnabled = environment.trackingEnabled;
  }

  ngOnInit(): void {
    this.isSysAdmin$ = this.store.pipe(select(fromStore.getIsSysAdmin));

    this.route.queryParamMap.pipe(untilComponentDestroyed(this)).subscribe((params: Params) => {
      this.queryParams = params;
      forkJoin([
        this.store.select((state) => state.user.isSalesOps).pipe(take(1)),
        this.store.select((state) => state.user.isSysAdmin).pipe(take(1)),
        this.store.select((state) => state.user.qubeScanModel).pipe(take(1)),
        this.store.pipe(select(fromStore.getAccount)).pipe(take(1)),
        this.store.pipe(select(fromStore.getUser)).pipe(take(1)),
        this.store.pipe(select(fromStore.getUserPreferences)).pipe(take(1)),
        this.store.pipe(select(fromStore.getMp)).pipe(take(1)),
        this.store.pipe(select(fromStore.getIsPartner)).pipe(take(1)),
        this.store.select(fromStore.getIsAdmin).pipe(take(1)),
        this.store.select(fromStore.getFeaturesState).pipe(take(1)),
      ]).subscribe(
        ([
          isSalesOps,
          isSysAdmin,
          qubeScanModel,
          userAccount,
          user,
          userPreferences,
          mp,
          isPartner,
          isAdministrator,
          featureFlags,
        ]) => {
          this.inactivityService.startWatching();
          this.loadingMp = false;
          this.canSelectMp = this.store.select(fromStore.getPartners).pipe(map(canSelectFromPartners), shareReplay(1));
          this.headerName$ = this.store.select((state) => state.user.headerName);
          this.viewerMode$ = this.store.select((state) => state.user.viewerMode);
          this.isSalesOps = isSalesOps;
          this.isSysAdmin = isSysAdmin;
          this.firmwareUpdateFeature$ = of(featureFlags.firmwareUpdate);
          this.notificationCenterFeature$ = of(featureFlags.notificationGroup);
          this.rmsVariationAnalysisFeature$ = of(featureFlags.rmsAnalysis);
          this.changeAccountSelectionFeature$ = of(featureFlags.changeAccountSelection);
          this.sarfiAnalysisFeature$ = of(featureFlags.sarfiAnalysis);

          this.user = user;
          if (this.user) {
            this.isEngineer = this.user.role.name === 'Engineer';
          }

          this.navOptions = [
            {
              route: '/fleet',
              tooltip: this.translateService.instant('navigation.fleet'),
              tracking: 'navigation.fleetOverview',
              icon: 'map-marker-multiple',
              disabled: false,
              dataCy: 'fleet-button',
            },
          ];

          if (!this.isSalesOps || this.isSysAdmin) {
            this.navOptions = [
              ...this.navOptions,
              {
                route: '/dashboard',
                tooltip: this.translateService.instant('navigation.dashboard'),
                tracking: 'navigation.dashboard',
                icon: 'dashboard-icon',
                canAccess: 'dashboard',
                featureKey: 'dashboard',
                disabled: null,
                dataCy: 'dashboard-button',
              },
              {
                route: '/events-list',
                tooltip: this.translateService.instant('navigation.eventsList'),
                tracking: 'navigation.eventsList',
                icon: 'format-list-bulleted',
                canAccess: 'whenCommissioned',
                featureKey: 'events',
                disabled: null,
                dataCy: 'eventList-button',
              },
              {
                route: '/charts',
                tooltip: this.translateService.instant('navigation.charts'),
                tracking: 'navigation.charts',
                icon: 'trends-graph',
                src: './assets/images/TrendsGraph_Icon.png',
                iconType: 'img',
                canAccess: 'whenCommissioned',
                featureKey: 'chart',
                disabled: null,
                size: '1.4rem',
                dataCy: 'charts-button',
              },
            ];
          }

          if (qubeScanModel !== 'PQube 3v' && qubeScanModel !== 'PQube 3vr') {
            this.navOptions = [
              ...this.navOptions,
              {
                route: '/energy-power',
                tooltip: this.translateService.instant('navigation.energyPower'),
                tracking: 'navigation.energyPower',
                icon: 'meter-icon',
                src: './assets/images/meter-icon.svg',
                iconType: 'img',
                canAccess: 'whenCommissioned',
                featureKey: 'energyAndPower',
                disabled: null,
                dataCy: 'energy-power-button',
              },
            ];
          }

          if (mp?.measurementPointTypeId === MpType.QUBE_SCAN || mp?.measurementPointTypeId === MpType.DRIVE_SCAN) {
            this.navOptions = [
              ...this.navOptions,
              {
                route: '/hf-emissions',
                tooltip: this.translateService.instant('navigation.hfEmissions'),
                tracking: 'navigation.hfEmissions',
                icon: 'hf-emission',
                canAccess: 'whenCommissioned',
                featureKey: 'hfEmissions',
                disabled: null,
                dataCy: 'hf-emissions-button',
              },
              {
                route: '/report-writer',
                tooltip: this.translateService.instant('navigation.reports'),
                tracking: 'navigation.reports',
                icon: 'clipboard-check-outline',
                canAccess: 'whenCommissioned',
                featureKey: 'reportCenter',
                disabled: null,
                badge$: this.store.pipe(select(fromStore.getUnseenReportCount)) as Observable<number>,
                hint: true,
                dataCy: 'reports-writer-icon',
              },
            ];
            this.possibleHint = true;
          } else {
            this.navOptions = [
              ...this.navOptions,
              {
                route: '/reports',
                tooltip: this.translateService.instant('navigation.reports'),
                tracking: 'navigation.reports',
                icon: 'clipboard-check-outline',
                canAccess: 'whenCommissioned',
                featureKey: 'reportCenter',
                disabled: null,
                badge$: this.store.pipe(select(fromStore.getRecommendationCountNumber)) as Observable<number>,
                hint: false,
                dataCy: 'reports-icon',
              },
            ];
            this.possibleHint = false;
          }

          if (
            mp?.measurementPointTypeId === MpType.QUBE_SCAN ||
            mp?.measurementPointTypeId === MpType.DRIVE_SCAN ||
            isSysAdmin
          ) {
            this.navOptions = [
              ...this.navOptions,
              {
                route: '/maintenance',
                tooltip: this.translateService.instant('navigation.maintenance'),
                tracking: 'navigation.maintenance',
                icon: 'cog',
                canAccess: 'whenCommissioned',
                featureKey: 'maintenance',
                disabled: null,
                dataCy: 'maintenance-icon',
              },
            ];
          }

          this.navOptions.forEach((option) => {
            if (option.canAccess) {
              if (option.canAccess === 'dashboard') {
                option.disabled = !(mp !== undefined && mp !== null && mp.mStatus !== MpState.ARCHIVED);
              } else if (option.canAccess === 'whenCommissioned') {
                option.disabled = !(mp && mp.mStatus > MpState.COLLECTING_DATA);
              }
            }

            // Disable Navigation based on feature flag
            if (option?.featureKey && featureFlags[option.featureKey]?.enabled === false) {
              option.disabled = true;
            }
          });

          this.menuItems = [
            {
              label: 'Edit My Profile',
              routerLink: '/profile',
            },
          ];
          if (isAdministrator) {
            this.menuItems = [
              ...this.menuItems,
              {
                label: 'Manage Members',
                command: (): void => {
                  this.navigateManageMember('/manage-members', userAccount?.id);
                },
              },
            ];
          } else if (this.user.role.name === 'Engineer' && isPartner) {
            this.menuItems = [
              ...this.menuItems,
              {
                label: 'View Members',
                command: (): void => {
                  this.navigateManageMember('/manage-members', userAccount?.id);
                },
              },
            ];
          }

          if (isSysAdmin) {
            this.menuItems = [
              ...this.menuItems,
              {
                label: 'Sales Ops',
                routerLink: '/sales-ops',
              },
              {
                label: 'System Admin',
                routerLink: '/system-admin',
              },
            ];
          }

          this.menuItems = [
            ...this.menuItems,
            {
              separator: true,
            },
            {
              label: 'Privacy Policy',
              command: (): void => {
                this.openPrivacyPolicy();
              },
            },
            {
              separator: true,
            },
            {
              label: 'Logout',
              command: (): void => {
                this.logout();
              },
            },
          ];

          if (this.isSysAdmin || !this.isSalesOps) {
            this.premiumFeatureItemSmall = [];
            if (featureFlags.rmsAnalysis?.navButtonVisible) {
              this.premiumFeatureItemSmall.push({
                label: this.translateService.instant('rmsVariationAnalysis.navigationButtonTitleMobile'),
                routerLink: '/rms-variation-analysis',
                queryParams: { type: FleetDisturbanceAnalysisType.RMS },
                command: () => {
                  this.menuClick();
                },
              });
            }
            if (featureFlags.sarfiAnalysis?.navButtonVisible) {
              this.premiumFeatureItemSmall.push({
                label: this.translateService.instant('sarfi.navigationButtonTitleMobile'),
                routerLink: '/rms-variation-analysis',
                queryParams: { type: FleetDisturbanceAnalysisType.SARFI },
                command: () => {
                  this.menuClick();
                },
              });
            }
            if (featureFlags.notificationGroup.navButtonVisible) {
              this.premiumFeatureItemSmall.push({
                label: this.translateService.instant('global.notificationCenter'),
                routerLink: '/notification',
                command: () => {
                  this.menuClick();
                },
              });
            }
            if (featureFlags.firmwareUpdate.navButtonVisible) {
              this.premiumFeatureItemSmall.push({
                label: this.translateService.instant('global.firmwareUpdate'),
                routerLink: '/admin',
                command: () => {
                  this.menuClick();
                },
              });
            }
          }

          this.firstName = user.firstName;
          this.userPrefs = cloneDeep(userPreferences);
          this.mpOptions = this.userPrefs?.lastMps ? [...this.userPrefs.lastMps] : [];
          this.mpOptions.reverse();

          if (
            params &&
            params.get('mpId') &&
            /^\d+$/.test(params.get('mpId')) &&
            params.get('account') &&
            /^\d+$/.test(params.get('account'))
          ) {
            this.mpId = parseInt(params.get('mpId'), 10);
            this.accountId = parseInt(params.get('account'), 10);
          } else if (Object.keys(userPreferences).length !== 0 && userPreferences.mpId) {
            this.mpId = userPreferences.mpId;
            this.accountId = userPreferences.account;
            this.router.navigate([], {
              queryParams: {
                ...params['params'],
                account: this.accountId.toString(),
                mpId: this.mpId.toString(),
              },
            });
          } else {
            this.canSelectMp
              .pipe(
                take(1),
                filter((x) => x)
              )
              .subscribe(() => this.onChangeSelection());
          }

          if (mp) {
            this.mp = mp;
            this.mpOptions.unshift({
              measurementPointId: this.mpId,
              accountName: mp.accountName,
              site: mp.locationName,
              mpName: mp.mpId,
              timezone: mp.timezone,
              accountId: mp.accountId,
            });

            if (
              this.userPrefs?.onBoarding?.isOnBoarding &&
              moment.duration(moment().diff(moment(this.userPrefs?.onBoarding?.timestamp))).asMinutes() > 10
            ) {
              this.store.dispatch(
                UserActions.updateUserPreferences({
                  payload: {
                    property: {
                      onBoarding: {
                        isOnBoarding: false,
                        timestamp: null,
                      },
                    },
                  },
                })
              );
            }
          } else if (this.accountId && this.mpId && this.user) {
            this.canSelectMp
              .pipe(
                take(1),
                filter((x) => x)
              )
              .subscribe(() => {
                if (!this.showChangeSelectionDialog) {
                  return this.onChangeSelection();
                }
              });
          }

          if (userPreferences?.welcomeMessage) {
            this.openWelcomeMessage();
          }

          this.store
            .select(fromStore.getLayoutHeadBannerHeight)
            .pipe(untilComponentDestroyed(this))
            .subscribe((height) => {
              this.currentHeadBannerHeight = height;
              this.setDynamicMargin(
                this.currentHeadBannerHeight +
                  (this.smallScreen ? this.DYNAMIC_MARGIN_SMALL : this.DYNAMIC_MARGIN_LARGE)
              );
            });
        }
      );
    });

    this.store
      .pipe(select(fromStore.getNewId))
      .pipe(untilComponentDestroyed(this))
      .subscribe((newsId) => {
        if (
          !this.userPrefs?.welcomeMessage &&
          !this.isSysAdmin &&
          this.userPrefs?.latestNewsId !== -1 &&
          this.userPrefs?.latestNewsId < newsId
        ) {
          this.openReleaseNews();

          this.store.dispatch(
            UserActions.updateUserPreferences({
              payload: { property: { latestNewsId: newsId } },
            })
          );
        }
      });

    this.store
      .pipe(select(fromStore.getHint))
      .pipe(untilComponentDestroyed(this))
      .subscribe((hint) => {
        this.hintFirmwareIsOpened =
          hint?.rms || hint?.createMp || hint?.associate
            ? false
            : hint?.hasOwnProperty('firmwareUpdate')
            ? hint.firmwareUpdate
            : true;

        this.hintNotificationIsOpened =
          hint?.rms || hint?.createMp || hint?.associate
            ? false
            : hint?.hasOwnProperty('notificationCenter')
            ? hint.notificationCenter
            : true;

        this.hintFirmwareAndNotificationIsOpened =
          hint?.rms || hint?.createMp || hint?.associate
            ? false
            : hint?.hasOwnProperty('firmwareAndNotification')
            ? hint.firmwareAndNotification
            : true;

        this.hintRmsIsOpened = hint?.rms || !hint?.hasOwnProperty('rms');

        this.hintReportIsOpened = this.possibleHint
          ? hint?.hasOwnProperty('reportWriter')
            ? hint.reportWriter
            : true
          : false;
      });
  }

  ngOnDestroy(): void {
    this.inactivityService.stopWatching();
    super.ngOnDestroy();
  }

  navClick(event): void {
    if (this.trackingEnabled) {
      this.eventTrackingService.trackEvent(event.tracking, {
        measurementPointId: parseInt(this.queryParams.params.mpId),
      });
    }
    this.router
      .navigate([event.route], {
        queryParams: {
          account: this.queryParams.params.account,
          mpId: this.queryParams.params.mpId,
        },
      })
      .then((value) => {
        if (value) {
          this.removeOnBoardingLoad();
        }
      });
  }

  onChangeSelection(): void {
    this.showChangeSelectionDialog = true;
  }

  selectMp(mp): void {
    this.loadingMp = true;
    this.store.dispatch(
      InitActions.changeSelection({
        payload: {
          point: mp,
          redirect: this.router.url.substring(0, this.router.url.indexOf('?')),
          onBoarding: { isOnBoarding: false, timestamp: null },
        },
      })
    );
  }

  openReleaseNews(): void {
    this.store.dispatch(NewsActions.openNews());
    this.showReleaseDialog = true;
  }

  openWelcomeMessage(): void {
    this.showWelcomeDialog = true;
  }
  navigateManageMember(url: string, accountId: number): void {
    this.menuClick();
    this.setPreviousUrl();
    this.router.navigate([url, accountId]);
  }

  private menuClick(): void {
    this.removeOnBoardingLoad();
  }

  private logout(): void {
    this.authService.logout();
    this.notificationsService.notify(this.translateService.instant('login.logged-out'));
  }

  private openPrivacyPolicy(): void {
    window.open('https://powerside.com/privacy-policy/', '_blank');
  }

  private removeOnBoardingLoad(): void {
    if (this.userPrefs?.onBoarding?.isOnBoarding) {
      this.store.dispatch(
        UserActions.updateUserPreferences({
          payload: {
            property: {
              onBoarding: {
                isOnBoarding: false,
                timestamp: null,
              },
            },
          },
        })
      );
    }
  }

  private setDynamicMargin(headBannerHeight: number): void {
    setTimeout(() => {
      this.dynamicMargin = {
        navigationTop: headBannerHeight + 'px',
        sectionTop: headBannerHeight + 'px',
      };
    });
  }

  private setPreviousUrl(): void {
    if (!this.router.url.includes('manage-member')) {
      const previousUrl = this.router.url.substring(0, this.router.url.indexOf('?'));
      this.store.dispatch(UserActions.setPreviousUrl({ payload: { url: previousUrl } }));
    }
  }
}
